import { pageTitle } from '@palaccount/config';
import { MFG_MESSAGES } from '@palaccount/contracts';
import { AlertTriangle, Calculator, Check, Gauge } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { Ltr, Qty } from '@/components/amount';
import { Pager } from '@/components/pager';
import { Gated } from '@/components/permission-gate';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { usePagedState, usePageRange } from '@/lib/paging';
import { useCan } from '@/lib/permissions';
import { localizeServerMessage, refusalMessage } from '@/lib/server-messages';
import { cn } from '@/lib/utils';

import { Typeahead } from '@/components/ui/typeahead';
import { useItemPicker } from '../inventory/item-picker';
import { useWarehouses } from '../inventory/stock-api';
import {
  usePlanDemand,
  usePlanNetRequirements,
  usePlanningMutations,
  usePlanReorderTargets,
  usePlannedOrders,
  useProductionPlan,
} from './api';
import { ManufacturingTabs } from './manufacturing-tabs';
import { formatQty, formatQtyWithUnit } from '@/lib/format';

import { barWidth, capacityBarClass, visibleWorkstation } from './mfg-format';

/**
 * MFG-03 — PRODUCTION PLANNING.
 *
 * "One screen, four stacked blocks" (§4), and *changed by owner ruling R1: the
 * prototype drew two versions of this screen — a one-card to-do list and a
 * four-block planning workbench. There is one screen, and everything on it is
 * on it.*
 *
 * BR-MFG-03-01a is the rule that shapes the demand card: "The demand list must
 * show EVERY source. No view may silently drop one" — the prototype's
 * simplified list dropped every forecast row with no notice, and "a planner
 * reads a short list and believes it is the list".
 */
/*
 * The block sizes. Each is what its block can show without the screen becoming
 * a scrollbar with a card around it — the demand table and the schedule are
 * full-width rows, the two side cards are short lists, and §5.7's band is a
 * wrap of pills rather than a table. They are constants rather than a URL
 * parameter for property 4 of `usePagedState`'s header.
 */
const DEMAND_PAGE_SIZE = 25;
const NET_PAGE_SIZE = 25;
const REQUISITION_PAGE_SIZE = 25;
const REORDER_PAGE_SIZE = 10;
const SCHEDULE_PAGE_SIZE = 25;

export function PlanningPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const canCreate = useCan('manufacturing.create');
  const { data: plan, isPending, isError } = useProductionPlan();
  /*
   * GEN-50 — §5.3's forecast picker, searched. Only FINISHED GOODS:
   * "components are never forecast directly; their demand comes from
   * exploding the recipe" (BR-MFG-03-02), and that is `type: 'product'` on
   * the server rather than a fold over a page.
   */
  const itemPicker = useItemPicker([], { type: 'product' });
  const { data: warehouses } = useWarehouses();
  const { run, addForecast, createFromDemand, convert } = usePlanningMutations();

  /*
   * FOUR blocks, four pagers, four names in the URL. §4 calls MFG-03 "one
   * screen, four stacked blocks", so they are four lists on one screen rather
   * than four screens — which is why `usePagedState` takes a name here and
   * nowhere else (see its header).
   *
   * The two net-requirement reads are the SAME route: §5.7's band is
   * `shortOnly`, which is BR-MFG-03-07's "a filtered view of the
   * net-requirements table, never a separate calculation" spelled as a
   * predicate the server applies BEFORE the LIMIT. Filtering it here, over the
   * page above, would have made the band mean "the short components on page 1".
   */
  const demandPaging = usePagedState(DEMAND_PAGE_SIZE, 'demand');
  const netPaging = usePagedState(NET_PAGE_SIZE, 'net');
  const reqPaging = usePagedState(REQUISITION_PAGE_SIZE, 'req');
  const reorderPaging = usePagedState(REORDER_PAGE_SIZE, 'reorder');
  const schedulePaging = usePagedState(SCHEDULE_PAGE_SIZE, 'schedule');

  const demand = usePlanDemand(demandPaging.page, DEMAND_PAGE_SIZE);
  const netRequirements = usePlanNetRequirements(netPaging.page, false, NET_PAGE_SIZE);
  const requisitions = usePlanNetRequirements(reqPaging.page, true, REQUISITION_PAGE_SIZE);
  const reorderTargets = usePlanReorderTargets(reorderPaging.page, REORDER_PAGE_SIZE);
  const plannedOrders = usePlannedOrders(schedulePaging.page, SCHEDULE_PAGE_SIZE);

  const demandRange = usePageRange(demand.data, DEMAND_PAGE_SIZE);
  const netRange = usePageRange(netRequirements.data, NET_PAGE_SIZE);
  const reqRange = usePageRange(requisitions.data, REQUISITION_PAGE_SIZE);
  const reorderRange = usePageRange(reorderTargets.data, REORDER_PAGE_SIZE);
  const scheduleRange = usePageRange(plannedOrders.data, SCHEDULE_PAGE_SIZE);
  const [error, setError] = useState<string | null>(null);
  const [forecast, setForecast] = useState({ itemId: '', qty: '', period: '' });
  const [params, setParams] = useState<{ horizonWeeks: 4 | 8 | 12; includeForecasts: boolean }>({
    horizonWeeks: 8,
    includeForecasts: true,
  });

  useEffect(() => {
    document.title = pageTitle(t('mfg.planning'));
  }, [t]);

  const defaultWarehouse = warehouses?.[0]?.id ?? '';
  // D46: the cause rides along — a structured refusal (alreadyConverted,
  // startedCannotMove, noActiveBom) localizes by key+params; anything else
  // keeps the server's own sentence, which is what PART 9 words.
  const fail = (caught: unknown) => setError(localizeServerMessage(t, refusalMessage(caught), caught));

  if (isPending) {
    return (
      <div className="grid gap-4">
        <ManufacturingTabs />
        <div className="h-64 animate-pulse rounded-md bg-muted" aria-busy />
      </div>
    );
  }
  if (isError || plan === undefined) {
    return (
      <div className="grid gap-4">
        <ManufacturingTabs />
        <p role="alert" className="py-8 text-center text-sm text-destructive">
          {t('common.loadFailed')}
        </p>
      </div>
    );
  }

  return (
    <div className="grid gap-4">
      <ManufacturingTabs />
      <h2 className="text-lg font-bold">{t('mfg.planning')}</h2>
      {error !== null ? (
        <p role="alert" className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </p>
      ) : null}

      {/* ── Region 1: demand on the left, MRP and reorder targets right ───
          NFR-17 — the prototype fixed this main area at two equal halves with
          no rule for a narrow window; `lg:grid-cols-2` IS the rule the NFR
          asks for: below lg the halves stack rather than squash. */}
      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>{t('mfg.demand')}</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-3">
            {demandRange.total === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground">
                {localizeServerMessage(t, MFG_MESSAGES.nothingWaiting)}
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('mfg.colItem')}</TableHead>
                    <TableHead className="text-end">{t('mfg.colQty')}</TableHead>
                    <TableHead>{t('mfg.colDue')}</TableHead>
                    <TableHead>{t('mfg.colSource')}</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(demand.data?.rows ?? []).map((row) => (
                    <TableRow key={row.key} className="odd:bg-muted/30">
                      <TableCell>
                        <div className="flex items-center gap-2 text-sm font-medium">
                          {row.itemName}
                          {row.isShort ? (
                            <span className="rounded-full bg-red-600 px-2 py-0.5 text-xs text-white">
                              {t('mfg.short')}
                            </span>
                          ) : null}
                        </div>
                        <Ltr
                          value={`${row.itemCode} · ${row.sourceRef ?? ''}`}
                          className="font-mono text-xs text-muted-foreground"
                        />
                      </TableCell>
                      {/* `N-4` / `N-14`: the unit on the ROW, never in the
                          header — this table merges three sources and its
                          items are counted in pieces, kilograms and boxes. */}
                      <TableCell className="text-end">
                        <Qty
                          value={row.qty}
                          precision={row.unitPrecision}
                          unit={row.unitSymbol}
                          className="text-sm"
                        />
                      </TableCell>
                      <TableCell>
                        <Ltr value={row.dueDate} className="font-mono text-xs" />
                      </TableCell>
                      <TableCell>
                        {/* Field 8: colour-coded per row — "the demand table is
                            a merged list of different kinds of demand, and the
                            colour is how you tell them apart". */}
                        <span
                          className={cn(
                            'rounded-full px-2 py-0.5 text-xs font-medium',
                            row.source === 'sales_order' &&
                              'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300',
                            row.source === 'forecast' && 'bg-muted text-muted-foreground',
                            row.source === 'reorder_point' &&
                              'bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-200',
                          )}
                        >
                          {t(`mfg.source.${row.source}`)}
                        </span>
                      </TableCell>
                      <TableCell>
                        {/* BR-MFG-03-20: this really creates a work order and
                            carries the row's item, quantity and due date into
                            it. "A button that only navigates must not be
                            built." */}
                        {/* No active recipe, no work order — for anybody. */}
                        {row.hasActiveBom ? (
                          <Gated
                            permission="manufacturing.create"
                            reasonKey="perm.cannot.createWorkOrders"
                          >
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() =>
                              void createFromDemand
                                .mutateAsync({
                                  itemId: row.itemId,
                                  qty: row.qty,
                                  dueDate: row.dueDate,
                                  warehouseId: defaultWarehouse,
                                })
                                .then((job) =>
                                  navigate(`/manufacturing/work-orders/${job.id}`),
                                )
                                .catch(fail)
                            }
                          >
                            {t('mfg.createWorkOrder')}
                          </Button>
                          </Gated>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}

            {/* BR-MFG-03-01a — "the demand list must show EVERY source; no view
                may silently drop one". A page is not a view that drops one: all
                three kinds are in this one list, and the total below says how
                many rows there are in it. */}
            <Pager
              page={demandPaging.page}
              pages={demandRange.pages}
              first={demandRange.first}
              last={demandRange.last}
              total={demandRange.total}
              onPage={demandPaging.setPage}
              label={t('mfg.demand')}
            />

            {/* §5.3's forecast strip. Only FINISHED GOODS are listed —
                "components are never forecast directly; their demand comes
                from exploding the recipe" (BR-MFG-03-02). */}
            <Gated permission="manufacturing.create" reasonKey="perm.cannot.addForecasts">
              <div className="grid gap-2 sm:grid-cols-[1fr_6rem_7rem_auto]">
                <Typeahead
                  label={t('mfg.colItem')}
                  value={forecast.itemId || null}
                  onChange={(id) => setForecast({ ...forecast, itemId: id ?? '' })}
                  options={itemPicker.options}
                  selected={itemPicker.optionFor(forecast.itemId)}
                  query={itemPicker.query}
                  onQueryChange={itemPicker.setQuery}
                  placeholder={t('common.select')}
                  emptyLabel={t('picker.noMatchingItem')}
                  loading={itemPicker.loading}
                  size="cell"
                />
                <Input
                  aria-label={t('mfg.colQty')}
                  placeholder={t('mfg.colQty')}
                  className="text-end font-mono"
                  value={forecast.qty}
                  onChange={(event) => setForecast({ ...forecast, qty: event.target.value })}
                />
                {/* Field 11: a MONTH, typed by hand as YYYY-MM. Not a picker. */}
                <Input
                  aria-label={t('mfg.period')}
                  placeholder="2026-08"
                  maxLength={7}
                  className="font-mono"
                  value={forecast.period}
                  onChange={(event) => setForecast({ ...forecast, period: event.target.value })}
                />
                <Button
                  variant="outline"
                  disabled={
                    forecast.itemId === '' || forecast.qty === '' || forecast.period === ''
                  }
                  onClick={() => {
                    setError(null);
                    void addForecast
                      .mutateAsync(forecast)
                      .then(() => setForecast({ itemId: '', qty: '', period: '' }))
                      .catch(fail);
                  }}
                >
                  {t('mfg.addForecast')}
                </Button>
              </div>
            </Gated>
          </CardContent>
        </Card>

        <div className="grid content-start gap-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calculator className="size-4 text-muted-foreground" aria-hidden />
                {t('mfg.mrpEngine')}
              </CardTitle>
            </CardHeader>
            <CardContent className="grid gap-3">
              <p className="text-sm text-muted-foreground">
                {/* Field 14: "Not yet run — click Recalculate" before the
                    first run; §15 calls that "the state a fresh screenshot
                    shows". */}
                {plan.lastCalculatedAt === null
                  ? t('mfg.notYetRun')
                  : t('mfg.lastCalculated', {
                      at: new Date(plan.lastCalculatedAt).toLocaleTimeString(),
                    })}
              </p>
              {/* §5.4's THREE required parameters, and only these three —
                  "timing choices beyond this are out of scope". */}
              <div className="grid gap-2 sm:grid-cols-2">
                <div className="grid gap-1.5">
                  <Label htmlFor="mrp-horizon">{t('mfg.horizon')}</Label>
                  <NativeSelect
                    id="mrp-horizon"
                    value={String(params.horizonWeeks)}
                    onChange={(event) =>
                      setParams({
                        ...params,
                        horizonWeeks: Number(event.target.value) as 4 | 8 | 12,
                      })
                    }
                  >
                    <option value="4">{t('mfg.weeks', { count: 4 })}</option>
                    <option value="8">{t('mfg.weeks', { count: 8 })}</option>
                    <option value="12">{t('mfg.weeks', { count: 12 })}</option>
                  </NativeSelect>
                </div>
                <div className="flex items-end gap-2">
                  <input
                    id="mrp-forecasts"
                    type="checkbox"
                    checked={params.includeForecasts}
                    onChange={(event) =>
                      setParams({ ...params, includeForecasts: event.target.checked })
                    }
                  />
                  <Label htmlFor="mrp-forecasts">{t('mfg.includeForecasts')}</Label>
                </div>
              </div>
              <Gated permission="manufacturing.edit" reasonKey="perm.cannot.runPlanning">
                <Button
                  onClick={() => {
                    setError(null);
                    void run.mutateAsync(params).catch(fail);
                  }}
                >
                  {t('mfg.recalculate')}
                </Button>
              </Gated>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{t('mfg.reorderTargets')}</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-2">
              {/* §5.5: "This card is about FINISHED GOODS, not components…
                  Reading it as a component list is the easiest mistake to make
                  on this screen." */}
              {reorderRange.total === 0 ? (
                <p className="py-4 text-center text-sm text-muted-foreground">
                  {t('mfg.noReorderTargets')}
                </p>
              ) : (
                (reorderTargets.data?.rows ?? []).map((row) => (
                  <div key={row.itemId} className="flex items-center gap-2 text-sm">
                    <span className="me-auto">{row.itemName}</span>
                    {/* `N-4` on BOTH figures, and the unit written ONCE at
                        the end of the run: on hand and the reorder point are
                        the same measurement of the same item, so repeating the
                        symbol between them is the repetition the rule does not
                        ask for — the same reading MFG-02's produced/target
                        pair takes. One `<Ltr>` island, because a number, a
                        label, a number and a symbol loose in an RTL line can
                        be reordered past each other. */}
                    <Ltr
                      value={`${t('mfg.onHand')} ${formatQty(row.onHand, row.unitPrecision)}  ROP ${formatQtyWithUnit(row.reorderPoint, row.unitPrecision, row.unitSymbol)}`}
                      className="font-mono text-xs text-muted-foreground"
                    />
                    {row.isBelow ? (
                      <AlertTriangle
                        className="size-4 text-amber-600"
                        aria-label={t('mfg.belowReorderPoint')}
                      />
                    ) : null}
                  </div>
                ))
              )}
              {reorderRange.pages > 1 ? (
                <Pager
                  page={reorderPaging.page}
                  pages={reorderRange.pages}
                  first={reorderRange.first}
                  last={reorderRange.last}
                  total={reorderRange.total}
                  onPage={reorderPaging.setPage}
                  label={t('mfg.reorderTargets')}
                />
              ) : null}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* ── Region 2: net requirements. HIDDEN until the plan has run. ──── */}
      {plan.lastCalculatedAt !== null ? (
        <Card>
          <CardHeader>
            <CardTitle>{t('mfg.netRequirements')}</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('mfg.colComponent')}</TableHead>
                    <TableHead className="text-end">{t('mfg.colGross')}</TableHead>
                    <TableHead className="text-end">{t('mfg.colOnHand')}</TableHead>
                    <TableHead className="text-end">{t('mfg.colIncoming')}</TableHead>
                    <TableHead className="text-end">{t('mfg.colNet')}</TableHead>
                    <TableHead className="text-end">{t('mfg.colLead')}</TableHead>
                    <TableHead>{t('mfg.colOrderBy')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(netRequirements.data?.rows ?? []).map((row) => {
                    const short = Number.parseFloat(row.netReq) > 0;
                    return (
                      <TableRow
                        key={row.itemId}
                        className={cn(short && 'bg-red-50 dark:bg-red-950/30')}
                      >
                        <TableCell>
                          <div className="text-sm">{row.itemName}</div>
                          <Ltr
                            value={row.itemCode}
                            className="font-mono text-xs text-muted-foreground"
                          />
                        </TableCell>
                        {/*
                          `N-4`'s decimals on all four figures, and the SYMBOL
                          once on the row — on Net req., the last of the four
                          and the one field 24 calls "the answer".

                          §5.6 draws SEVEN columns and no Unit column, so there
                          is nowhere else on the row to put it; and the four
                          figures are one subtraction of one item's stock —
                          `net = gross − on hand − incoming` — so a symbol on
                          each would repeat three times what the row already
                          says once. That is the same reading INV-05's three
                          stock columns take, from the other side: there the
                          spec gives a Unit column and the figures carry
                          decimals only.
                        */}
                        <TableCell className="text-end font-mono text-sm">
                          {formatQty(row.grossReq, row.unitPrecision)}
                        </TableCell>
                        <TableCell className="text-end font-mono text-sm">
                          {formatQty(row.onHand, row.unitPrecision)}
                        </TableCell>
                        <TableCell className="text-end font-mono text-sm">
                          {formatQty(row.incoming, row.unitPrecision)}
                        </TableCell>
                        {/* Field 24: "Shown bolder and larger than the other
                            numbers, and coloured red when above zero." */}
                        <TableCell
                          className={cn(
                            'text-end font-mono text-base font-bold',
                            short && 'text-red-600 dark:text-red-400',
                          )}
                        >
                          <Qty
                            value={row.netReq}
                            precision={row.unitPrecision}
                            unit={row.unitSymbol}
                            className="text-base font-bold"
                          />
                        </TableCell>
                        <TableCell className="text-end font-mono text-sm">{row.leadDays}d</TableCell>
                        <TableCell>
                          {row.orderBy === null ? (
                            '—'
                          ) : (
                            <span
                              className={cn(
                                'font-mono text-xs',
                                row.isLate && 'font-semibold text-red-600 dark:text-red-400',
                              )}
                              // Parameterised — the date is the argument, and
                              // it arrives already formatted, so no ICU number
                              // or date skeleton is introduced here.
                              title={
                                row.isLate ? t('mfg.lateOrder', { on: row.orderBy }) : undefined
                              }
                            >
                              {row.orderBy}
                            </span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
            <Pager
              page={netPaging.page}
              pages={netRange.pages}
              first={netRange.first}
              last={netRange.last}
              total={netRange.total}
              onPage={netPaging.setPage}
              label={t('mfg.netRequirements')}
            />

            {/* §5.7's band, INSIDE the same card's footer, on an amber wash.
                EC-19-34, honestly: the band is still ADVICE with no "Create
                purchase requests" action — the EC proposed one, the MFG-03
                chapter's own BR set never adopted it (only work-order
                creation got a button, BR-MFG-03-20), so wiring purchasing in
                stays an owner decision, flagged in the rule-queue report.
                BR-MFG-03-07: "a filtered view of the net-requirements table,
                never a separate calculation" — and it still is one. It is the
                same route as the table above, asked with `shortOnly`, so the
                two cannot drift; what it must NOT be is this page filtered in
                the browser, which would quietly narrow the band to whatever
                page of the table happened to be open. Its own total is the
                count of every short component, not the count on this page. */}
            <div className="rounded-lg bg-amber-50 p-3 dark:bg-amber-950/40">
              <p className="mb-2 text-sm font-medium">{t('mfg.suggestedRequisitions')}</p>
              {reqRange.total === 0 ? (
                <p className="flex items-center gap-2 text-sm text-emerald-700 dark:text-emerald-400">
                  <Check className="size-4" aria-hidden />{' '}
                  {localizeServerMessage(t, MFG_MESSAGES.noShortfalls)}
                </p>
              ) : (
                <>
                  <ul className="flex flex-wrap gap-2">
                    {(requisitions.data?.rows ?? []).map((row) => (
                      <li key={row.itemId} className="rounded-full border bg-background px-3 py-1 text-xs">
                        {row.itemName} ·{' '}
                        {/* §5.7's chip is its own card and has no
                            net-requirements row beside it, so it carries the
                            symbol itself — the same reason INV-05's expander
                            does where its table does not. Field 28: "Decimal
                            (3 dp)", which is `N-4`'s decimals by another name. */}
                        <span className="font-mono text-red-600 dark:text-red-400">
                          {t('mfg.order')}{' '}
                          <Ltr value={formatQtyWithUnit(row.netReq, row.unitPrecision, row.unitSymbol)} />
                        </span>{' '}
                        · <span className="font-mono">{t('mfg.by')} {row.orderBy}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-3">
                    <Pager
                      page={reqPaging.page}
                      pages={reqRange.pages}
                      first={reqRange.first}
                      last={reqRange.last}
                      total={reqRange.total}
                      onPage={reqPaging.setPage}
                      label={t('mfg.suggestedRequisitions')}
                    />
                  </div>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      ) : null}

      {/* ── Region 3: capacity, ALWAYS shown ─────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Gauge className="size-4 text-muted-foreground" aria-hidden />
            <span className="me-auto">{t('mfg.capacity')}</span>
            {/* Field 34 / BR-MFG-03-13: one red pill when ANY resource is
                over — a roll-up, not a per-row flag. The "any one" decision is
                the server's `isOverloaded`; this is the pill the rule requires
                to appear, and it appears on that flag alone. Per-row overage
                is a separate, additive signal (`row.overBy` below) and is not
                what this rule asks for. */}
            {plan.isOverloaded ? (
              <span className="rounded-full bg-red-600 px-2 py-0.5 text-xs font-semibold text-white">
                {t('mfg.overloaded')}
              </span>
            ) : null}
          </CardTitle>
        </CardHeader>
        <CardContent className="grid gap-3">
          {plan.capacity.length === 0 ? (
            <p className="py-4 text-center text-sm text-muted-foreground">{t('mfg.noCapacity')}</p>
          ) : (
            plan.capacity.map((row) => (
              <div key={row.resource} className="grid gap-1">
                <div className="flex items-center gap-2 text-sm">
                  <span className="me-auto">{visibleWorkstation(row.resource)}</span>
                  {row.overBy !== null ? (
                    <span className="text-xs font-medium text-red-600 dark:text-red-400">
                      {t('mfg.overBy', { hours: row.overBy })}
                    </span>
                  ) : null}
                  {/* Field 32: load carries ONE decimal, available NONE, and
                      the `h` is written after the SECOND number only. NFR-01:
                      the run stays ONE island (separators between separate
                      islands reorder in RTL) and the `h`/`%` come from the
                      dictionary rather than the keyboard. */}
                  <Ltr
                    value={`${row.loadHours} / ${t('unit.hours', { value: row.availableHours })} · ${t('unit.percent', { value: row.percent })}`}
                    className="font-mono text-xs text-muted-foreground"
                  />
                </div>
                <div className="h-2.5 w-full overflow-hidden rounded-full bg-muted">
                  <div
                    className={cn('h-full', capacityBarClass(row.band))}
                    style={{ width: barWidth(row.percent) }}
                  />
                </div>
              </div>
            ))
          )}
        </CardContent>
      </Card>

      {/* ── Region 4: the planned entries ────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle>{t('mfg.productionSchedule')}</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-2">
          {scheduleRange.total === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">{t('mfg.noPlanned')}</p>
          ) : (
            (plannedOrders.data?.rows ?? []).map((row) => (
              <div key={row.id} className="flex flex-wrap items-center gap-3 rounded-lg border p-3">
                <span className="text-sm">
                  {/* `N-4` on the schedule row: one item, one quantity, its
                      own unit. */}
                  {row.itemName} ·{' '}
                  <Qty value={row.qty} precision={row.unitPrecision} unit={row.unitSymbol} />{' '}
                  ·{' '}
                  <Ltr value={row.startDate} className="text-xs text-muted-foreground" />
                </span>
                <span className="me-auto" />
                {row.status === 'converted' ? (
                  <>
                    <span className="flex items-center gap-1 text-xs text-emerald-700 dark:text-emerald-400">
                      <Check className="size-3" aria-hidden /> {t('mfg.converted')}
                    </span>
                    {/* §5.10 [fixed]: "once converted, the Convert button must
                        be replaced by an Open WO-2026-0087 link." */}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        void navigate(`/manufacturing/work-orders/${row.workOrderId!}`)
                      }
                    >
                      {t('mfg.open')} {row.workOrderCode}
                    </Button>
                  </>
                ) : canCreate ? (
                  <Button
                    size="sm"
                    onClick={() => {
                      setError(null);
                      void convert
                        .mutateAsync({
                          plannedOrderId: row.id,
                          warehouseId: defaultWarehouse,
                        })
                        .then((job) => navigate(`/manufacturing/work-orders/${job.id}`))
                        .catch(fail);
                    }}
                  >
                    {t('mfg.convertToWo')}
                  </Button>
                ) : null}
              </div>
            ))
          )}
          <Pager
            page={schedulePaging.page}
            pages={scheduleRange.pages}
            first={scheduleRange.first}
            last={scheduleRange.last}
            total={scheduleRange.total}
            onPage={schedulePaging.setPage}
            label={t('mfg.productionSchedule')}
          />
        </CardContent>
      </Card>
    </div>
  );
}
